import React from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "./chatbot/app/queryClient";
import { AppLayout as ChatbotLayout } from "./chatbot/components/AppLayout";
import { LoginPage as ChatbotLoginPage } from "./chatbot/pages/LoginPage";
import { DashboardPage as ChatbotDashboardPage } from "./chatbot/pages/DashboardPage";
import { ConversationsPage } from "./chatbot/pages/ConversationsPage";
import { ConversationDetailPage } from "./chatbot/pages/ConversationDetailPage";
import { LeadsPage } from "./chatbot/pages/LeadsPage";
import { AllowedTopicsPage } from "./chatbot/pages/AllowedTopicsPage";
import { OffersPage } from "./chatbot/pages/OffersPage";
import Header from "./components/Header";
import Footer from "./components/Footer";
import ScrollToTop from "./components/ScrollToTop";
import Home from "./pages/Home";
import ChiSiamo from "./pages/ChiSiamo";
import Offerte from "./pages/Offerte";
import Contattaci from "./pages/Contattaci";
import Faq from "./pages/Faq";
import PrivacyPolicy from "./pages/legal/PrivacyPolicy";
import CookiePolicy from "./pages/legal/CookiePolicy";
import TermsAndConditions from "./pages/legal/TermsAndConditions";
import ElencoFornitori from "./pages/ElencoFornitori";
import NotFound from "./pages/NotFound";
import QuizLanding from "./pages/QuizLanding";
import ChatbotLanding from "./pages/ChatbotLanding";
import LandingDirectCall1 from "./pages/LandingDirectCall1";
import LandingDirectCall2 from "./pages/LandingDirectCall2";
import LandingDirectCall3 from "./pages/LandingDirectCall3";
import LandingDirectCall4 from "./pages/LandingDirectCall4";
import CookieBanner from "./components/CookieBanner";

const MainLayout: React.FC = () => (
  <div className="flex flex-col min-h-screen mt-10">
    <Helmet>
      <title>Servizio Bolletta — Consulenza Gratuita Offerte Luce, Gas e Internet</title>
      <meta
        name="description"
        content="Servizio Bolletta è un servizio indipendente di comparazione e consulenza commerciale per offerte luce, gas e internet. Consulenza gratuita, nessun obbligo. Gestito da GT Energie Srl."
      />
      <meta
        name="keywords"
        content="confronto offerte luce, confronto offerte gas, confronto offerte internet, consulenza gratuita energia, comparazione tariffe"
      />
      <link rel="canonical" href="https://serviziobolletta.it" />
    </Helmet>
    <Header />
    <main className="flex-grow">
      <Outlet />
    </main>
    <Footer />
  </div>
);

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
    <HelmetProvider>
      <Router>
        <ScrollToTop />
        <CookieBanner />
        <Routes>
          {/* Landing Quiz — layout autonomo */}
          <Route path="/offerta" element={<QuizLanding />} />
          <Route path="/chatbot" element={<ChatbotLanding />} />
          <Route path="/landing-direct-call-1" element={<LandingDirectCall1 />} />
          <Route path="/landing-direct-call-2" element={<LandingDirectCall2 />} />
          <Route path="/landing-direct-call-3" element={<LandingDirectCall3 />} />
          <Route path="/landing-direct-call-4" element={<LandingDirectCall4 />} />

          <Route
            path="/dahsbot/login"
            element={
              <>
                <Helmet>
                  <title>Accesso dashboard | Servizio Bolletta</title>
                  <meta name="robots" content="noindex, nofollow" />
                </Helmet>
                <ChatbotLoginPage />
              </>
            }
          />
          <Route
            path="/dahsbot"
            element={
              <>
                <Helmet>
                  <title>Dashboard chatbot | Servizio Bolletta</title>
                  <meta name="robots" content="noindex, nofollow" />
                </Helmet>
                <ChatbotLayout />
              </>
            }
          >
            <Route index element={<ChatbotDashboardPage />} />
            <Route path="conversations" element={<ConversationsPage />} />
            <Route path="conversations/:id" element={<ConversationDetailPage />} />
            <Route path="leads" element={<LeadsPage />} />
            <Route path="allowed-topics" element={<AllowedTopicsPage />} />
            <Route path="offerte" element={<OffersPage />} />
          </Route>

          {/* Sito principale con header/footer */}
          <Route element={<MainLayout />}>
            <Route
              index
              element={
                <>
                  <Helmet>
                    <title>Servizio Bolletta — Consulenza Gratuita Offerte Luce, Gas e Internet</title>
                    <meta name="description" content="Confronta le offerte luce, gas e internet con un consulente Servizio Bolletta. Servizio indipendente, gratuito e senza obbligo. Gestito da GT Energie Srl." />
                    <link rel="canonical" href="https://serviziobolletta.it" />
                  </Helmet>
                  <Home />
                </>
              }
            />
            <Route
              path="/chi-siamo"
              element={
                <>
                  <Helmet>
                    <title>Chi Siamo — Servizio Bolletta | Consulenza Indipendente</title>
                    <meta name="description" content="Servizio Bolletta è gestito da GT Energie Srl. Siamo un servizio indipendente di comparazione offerte luce, gas e internet. Consulenza gratuita, nessun obbligo." />
                    <link rel="canonical" href="https://serviziobolletta.it/chi-siamo" />
                  </Helmet>
                  <ChiSiamo />
                </>
              }
            />
            <Route
              path="/offerte"
              element={
                <>
                  <Helmet>
                    <title>Confronta Offerte Luce, Gas e Internet | Servizio Bolletta</title>
                    <meta name="description" content="Confronta le offerte luce, gas e internet disponibili sul mercato. Consulenza gratuita e senza obbligo di sottoscrizione. Servizio indipendente." />
                    <link rel="canonical" href="https://serviziobolletta.it/offerte" />
                  </Helmet>
                  <Offerte />
                </>
              }
            />
            <Route
              path="/contattaci"
              element={
                <>
                  <Helmet>
                    <title>Richiedi Consulenza Gratuita | Servizio Bolletta</title>
                    <meta name="description" content="Richiedi una consulenza gratuita e senza impegno per confrontare le offerte luce, gas e internet. Servizio indipendente gestito da GT Energie Srl." />
                    <link rel="canonical" href="https://serviziobolletta.it/contattaci" />
                  </Helmet>
                  <Contattaci />
                </>
              }
            />
            <Route
              path="/faq"
              element={
                <>
                  <Helmet>
                    <title>Domande Frequenti (FAQ) | Servizio Bolletta</title>
                    <meta name="description" content="Risposte alle domande più frequenti su Servizio Bolletta. Siamo un servizio indipendente di comparazione, gratuito e senza obbligo." />
                    <link rel="canonical" href="https://serviziobolletta.it/faq" />
                  </Helmet>
                  <Faq />
                </>
              }
            />
            <Route
              path="/privacy-policy"
              element={
                <>
                  <Helmet>
                    <title>Privacy Policy | Servizio Bolletta — GT Energie Srl</title>
                    <meta name="description" content="Informativa sul trattamento dei dati personali di Servizio Bolletta, gestito da GT Energie Srl. P.IVA IT02812720809." />
                    <link rel="canonical" href="https://serviziobolletta.it/privacy-policy" />
                  </Helmet>
                  <PrivacyPolicy />
                </>
              }
            />
            <Route
              path="/cookie-policy"
              element={
                <>
                  <Helmet>
                    <title>Cookie Policy | Servizio Bolletta — GT Energie Srl</title>
                    <meta name="description" content="Cookie Policy di Servizio Bolletta. Informazioni sui cookie utilizzati e su come gestirli. Gestito da GT Energie Srl." />
                    <link rel="canonical" href="https://serviziobolletta.it/cookie-policy" />
                  </Helmet>
                  <CookiePolicy />
                </>
              }
            />
            <Route
              path="/termini-e-condizioni"
              element={
                <>
                  <Helmet>
                    <title>Termini e Condizioni | Servizio Bolletta — GT Energie Srl</title>
                    <meta name="description" content="Termini e condizioni d'uso del sito Servizio Bolletta. Servizio indipendente di consulenza e comparazione offerte, gestito da GT Energie Srl." />
                    <link rel="canonical" href="https://serviziobolletta.it/termini-e-condizioni" />
                  </Helmet>
                  <TermsAndConditions />
                </>
              }
            />
            <Route
              path="/elenco-fornitori-societa-terze"
              element={
                <>
                  <Helmet>
                    <title>Elenco Fornitori e Società Terze | Servizio Bolletta</title>
                    <meta name="description" content="Elenco completo dei fornitori e società terze partner di Servizio Bolletta. Gestito da GT Energie Srl." />
                    <link rel="canonical" href="https://serviziobolletta.it/elenco-fornitori-societa-terze" />
                  </Helmet>
                  <ElencoFornitori />
                </>
              }
            />
            <Route path="/404" element={<NotFound />} />
            <Route path="*" element={<Navigate to="/404" replace />} />
          </Route>
        </Routes>
      </Router>
    </HelmetProvider>
    </QueryClientProvider>
  );
};

export default App;
